import React, { useCallback, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { parse } from 'qs'

import { TableUrlPagination } from '../../../../ui'
import { APPS_TABLE_COLUMNS, APPS_TABLE_ACTIONS, DEFAULT_QUERY } from '../../constants'
import { useNonCaptablesValidationContext } from '../../contexts'
import { TableActions } from '../table-actions'
import { nonCaptablesValidationDataPreparation } from '../../logics'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const AppsTable = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const combos = useSelector((state) => state.combos)
  const location = useLocation()
  const {
    state: {
      data,
      pagination: { total_solicitudes: totalEntries },
      loading,
      search,
    },
    nonCaptablesValidationSelect,
    nonCaptablesValidationTableCheck,
    nonCaptablesValidationLoad,
  } = useNonCaptablesValidationContext()

  useEffect(() => {
    nonCaptablesValidationTableCheck([])
  }, [])

  useEffect(() => {
    const urlSearchParams = parse(location.search.split('?')[1])
    const searchParams = {
      ...DEFAULT_QUERY,
      ...urlSearchParams,
    }
    nonCaptablesValidationLoad(searchParams)
  }, [location.search, nonCaptablesValidationLoad])

  const handleSelectRows = useCallback(
    (rows) => {
      nonCaptablesValidationTableCheck(rows)
    },
    [nonCaptablesValidationTableCheck]
  )

  const formattedColumns = useMemo(
    () =>
      [...APPS_TABLE_COLUMNS, ...APPS_TABLE_ACTIONS].map((column) => {
        const baseColumn = {
          accessorKey: column.field,
          header: formatMessage({
            id: `pages.nonCaptablesValidation.table.columns.${column.field}.title`,
          }),
        }

        if (column.field === 'view') {
          return {
            ...baseColumn,
            header: formatMessage({ id: `global.${column.field}` }),
            Cell: (props) => <TableActions {...props} />,
            muiTableBodyCellProps: { align: 'center' },
          }
        }

        if (column.field === 'active') {
          return {
            ...baseColumn,
            header: formatMessage({ id: `global.${column.field}` }),
            // Assuming this should be a different component or logic
            Cell: ({ cell }) => (cell.getValue() ? 'Sí' : 'No'),
            muiTableBodyCellProps: { align: 'center' },
          }
        }

        if (column.comboId && combos[column.comboId]?.data) {
          const lookup = getLookup(combos[column.comboId].data)
          return {
            ...baseColumn,
            Cell: ({ cell }) => {
              const value = cell.getValue()
              return <span>{lookup[value] ?? value ?? '--'}</span>
            },
          }
        }

        return {
          ...baseColumn,
          Cell: ({ cell }) => <span>{cell.getValue() ?? '--'}</span>,
        }
      }),
    [combos, formatMessage]
  )

  const processedData = useMemo(
    () => nonCaptablesValidationDataPreparation(data, { intl }, combos),
    [data, intl, combos]
  )

  const tableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: true,
      fixedColumns: {
        left: 1,
      },
      draggable: false,
    }),
    []
  )

  const handleRowClick = useCallback(
    (_, row) => {
      nonCaptablesValidationSelect(row.id, row.certificateType)
    },
    [nonCaptablesValidationSelect]
  )

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={tableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={loading}
      onRowClick={handleRowClick}
      onSelectionChange={handleSelectRows}
    />
  )
}
